import {
  GlobalStateRecord,
  useGlobalStateBatch,
  useOrganisation,
} from "@inrange/building-manager-api-client";
import {
  Organisation,
  OrgInvoiceProps as OrgInvoicePropsType,
} from "@inrange/building-manager-api-client/models-organisation";
import { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

const OrgInvoicePropsCard = ({
  organisation,
}: {
  organisation: Organisation;
}) => {
  const { updateOrganisation } = useOrganisation(organisation.id);
  const { globalStateForType } = useGlobalStateBatch();

  const [props, setProps] = useState<OrgInvoicePropsType>(
    organisation.invoiceProps
  );
  const [hasChanges, setHasChanges] = useState(false);

  const updateField = (field: keyof OrgInvoicePropsType, value: any) => {
    setProps((prev) => ({ ...prev, [field]: value || null }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateOrganisation.mutate(
      { organisation: { invoiceProps: props } },
      { onSuccess: () => setHasChanges(false) }
    );
  };

  const bankAccountOptions: GlobalStateRecord[] = globalStateForType("bank_account");
  const tcsOptions: GlobalStateRecord[] = globalStateForType("terms_and_conditions");
  const footerOptions: GlobalStateRecord[] = globalStateForType("invoice_footer_text");
  const vatNoticeOptions: GlobalStateRecord[] = globalStateForType("vat_notice");
  const invoiceQuestionsOptions: GlobalStateRecord[] = globalStateForType("invoice_questions");

  return (
    <CollapsibleCard title="Invoice properties" initialOpen={false}>
      <Row className="mb-3">
        <Col sm={6}>
          <Form.Group className="mb-3">
            <Form.Label>Legal entity name</Form.Label>
            <Form.Control
              type="text"
              value={props.legalEntityName || ""}
              onChange={(e) => updateField("legalEntityName", e.target.value)}
              placeholder="e.g. Acme Ltd"
            />
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group className="mb-3">
            <Form.Label>Entity address</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={props.entityAddress || ""}
              onChange={(e) => updateField("entityAddress", e.target.value)}
              placeholder="e.g. 123 Main Street&#10;London&#10;EC1V 2NX"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        {organisation.orgType === "Landlord" && (
          <Col sm={3}>
            <Form.Group className="mb-3">
              <Form.Label>Statement period</Form.Label>
              <Form.Select
                value={props.statementPeriod}
                onChange={(e) => updateField("statementPeriod", e.target.value)}
              >
                <option value="quarterly">Quarterly</option>
                <option value="annualy">Annually</option>
              </Form.Select>
            </Form.Group>
          </Col>
        )}
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>Invoice period</Form.Label>
            <Form.Select
              value={props.invoicePeriod}
              onChange={(e) => updateField("invoicePeriod", e.target.value)}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>Invoice date</Form.Label>
            <Form.Select
              value={props.invoiceDate}
              onChange={(e) => updateField("invoiceDate", e.target.value)}
            >
              <option value="start_of_period">Start of period</option>
              <option value="end_of_period">End of period</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>Days issued to due</Form.Label>
            <Form.Control
              type="number"
              value={props.daysIssuedToDue}
              onChange={(e) =>
                updateField(
                  "daysIssuedToDue",
                  e.target.value ? parseInt(e.target.value) : 15
                )
              }
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>Days counted type</Form.Label>
            <Form.Select
              value={props.daysCountedType}
              onChange={(e) => updateField("daysCountedType", e.target.value)}
            >
              <option value="calendar">Calendar days</option>
              <option value="business">Business days</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Bank account</Form.Label>
            <Form.Select
              value={props.bankAccountId}
              onChange={(e) => updateField("bankAccountId", e.target.value)}
            >
              {bankAccountOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Terms & conditions</Form.Label>
            <Form.Select
              value={props.termsAndConditionsId}
              onChange={(e) =>
                updateField("termsAndConditionsId", e.target.value)
              }
            >
              {tcsOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Invoice footer text</Form.Label>
            <Form.Select
              value={props.invoiceFooterTextId}
              onChange={(e) =>
                updateField("invoiceFooterTextId", e.target.value)
              }
            >
              {footerOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>VAT notice</Form.Label>
            <Form.Select
              value={props.vatNoticeId}
              onChange={(e) => updateField("vatNoticeId", e.target.value)}
            >
              {vatNoticeOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3">
            <Form.Label>Invoice questions</Form.Label>
            <Form.Select
              value={props.invoiceQuestionsId}
              onChange={(e) =>
                updateField("invoiceQuestionsId", e.target.value)
              }
            >
              {invoiceQuestionsOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Button
        variant="success"
        onClick={handleSave}
        disabled={!hasChanges || updateOrganisation.isLoading}
      >
        {updateOrganisation.isLoading ? "Saving..." : "Save Invoice Properties"}
      </Button>
    </CollapsibleCard>
  );
};

export default OrgInvoicePropsCard;
